import { useState, Fragment } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  PiArrowLeft,
  PiArrowRight,
  PiArrowsDownUp,
  PiBookmarkSimple,
  PiBuildings,
  PiCopy,
  PiCaretDown,
  PiColumns,
  PiDotsSixVertical,
  PiFunnel,
  PiHandshake,
  PiListDashes,
  PiPencilSimple,
  PiPlus,
  PiRows,
  PiTarget,
  PiUsers,
  PiX,
} from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from 'components/UI/popover'
import { runQuery } from './engine'
import { frameForSources, SOURCES } from './frames'
import type { FrameDef, SourceId } from './frames'
import { REPORTING_PAGE_SHELL } from './page-shell'
import { getSavedView, saveView, updateSavedView } from './saved-views'
import { useKpis, saveKpi } from './kpis'
import type { Kpi } from './kpis'
import { SaveDialog } from './components/SaveDialog'
import type { SaveResult } from './components/SaveDialog'
import { ReportTable } from './components/ReportTable'
import { MultiSelectList } from './components/MultiSelectList'
import { MetricView } from './components/MetricView'
import { RankedListView } from './components/RankedListView'
import { ReportChart } from './components/ReportChart'
import type { ReportChartType } from './components/ReportChart'
import {
  ViewSwitcher,
  availableViews,
  resolveView,
} from './components/ViewSwitcher'
import type { ReportView } from './components/ViewSwitcher'
import type { Filter, ViewConfig } from './view-config'
import {
  makeStep,
  stepLabel,
  toConfig,
  connectorLabel,
  sourcesFromFrameId,
  stepFromConfig,
  upstreamFilters,
} from './builder-model'
import type { Panel, AggKind, MetricAgg, Step, Adder } from './builder-model'
import {
  inFilterFields,
  inFilterValues,
  setInFilter,
  removeInFilter,
  numericFilterFields,
  numericFilterOf,
  setNumericFilter,
  removeNumericFilter,
  dateFilterFields,
  dateFilterOf,
  setDateFilter,
  removeDateFilter,
} from './pipeline-ops'
import type { DateOp } from './pipeline-ops'
import { MenuItem, MenuSection } from './components/FilterMenu'
import {
  NumericFilterRow,
  DateCompareFilterRow,
} from './components/ScalarFilters'
import { MatchedSelect } from './components/MatchedSelect'

/**
 * Report builder — UX prototype.
 *
 * Left panel: source picker (multi-select; inner-joined where keys align).
 * Right: flow diagram strip + active step detail.
 */

// ── Filter pill types ─────────────────────────────────────────────────────────

type Pill =
  | { kind: 'cat'; field: string }
  | { kind: 'num'; field: string }
  | { kind: 'date'; field: string }
const pillKey = (p: Pill): string => `${p.kind}:${p.field}`
const asVc = (filters: Filter[]): ViewConfig => ({
  id: '',
  name: '',
  description: '',
  kind: 'list',
  frame: 'deal',
  filters,
  chart: 'table-only',
  knobs: [],
})

// ── ToolbarIcon ───────────────────────────────────────────────────────────────

function ToolbarIcon({
  icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  active?: boolean
  badge?: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={cn(
        'relative flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-brand-active bg-brand-active/10 text-brand-active'
          : 'border-line bg-surface text-fg-secondary hover:bg-surface-hover hover:text-fg'
      )}
    >
      {icon}
      <span>{label}</span>
      {badge != null && badge > 0 && (
        <span className="ml-0.5 flex size-4 items-center justify-center rounded-full bg-brand-active text-[10px] text-white">
          {badge}
        </span>
      )}
    </button>
  )
}

// ── MetricFields ──────────────────────────────────────────────────────────────

function MetricFields({
  agg,
  field,
  frame,
  onChange,
}: {
  agg: MetricAgg
  field: string
  frame: FrameDef
  onChange: (agg: MetricAgg, field: string) => void
}) {
  const needsField = agg === 'sum' || agg === 'avg'
  const AGG_OPTIONS = [
    { value: 'count', label: 'Count' },
    { value: 'sum', label: 'Sum of' },
    { value: 'avg', label: 'Avg of' },
  ]
  return (
    <div className="flex items-center gap-2">
      <MatchedSelect
        ariaLabel="Aggregation type"
        options={AGG_OPTIONS}
        value={AGG_OPTIONS.find((o) => o.value === agg)}
        onChange={(v) => onChange(v as MetricAgg, field)}
      />
      {needsField && (
        <MatchedSelect
          ariaLabel="Aggregation field"
          options={frame.numFields.map((f) => ({
            value: f,
            label: frame.labels[f] ?? f,
          }))}
          value={{ value: field, label: frame.labels[field] ?? field }}
          onChange={(v) => onChange(agg, v)}
        />
      )}
    </div>
  )
}

// ── AggForm ───────────────────────────────────────────────────────────────────

const AGG_KINDS: { value: AggKind; label: string; desc: string }[] = [
  { value: 'group-by', label: 'Group by', desc: 'Group rows by a dimension' },
  { value: 'summary', label: 'Summary', desc: 'Single-row rollup of all rows' },
  { value: 'pivot', label: 'Pivot', desc: 'Cross-tab: rows × columns' },
]

function AggForm({
  draft,
  frame,
  onChange,
  onApply,
  onCancel,
}: {
  draft: Adder
  frame: FrameDef
  onChange: (next: Adder) => void
  onApply: () => void
  onCancel: () => void
}) {
  return (
    <div className="w-full max-w-lg rounded-xl border border-brand-active/30 bg-surface p-4 shadow-sm">
      <div className="mb-4 flex gap-2">
        {AGG_KINDS.map((k) => (
          <button
            key={k.value}
            type="button"
            onClick={() => onChange({ ...draft, aggKind: k.value })}
            title={k.desc}
            className={cn(
              'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
              draft.aggKind === k.value
                ? 'border-brand-active bg-brand-active/10 text-brand-active'
                : 'border-line bg-surface text-fg-secondary hover:bg-surface-hover hover:text-fg'
            )}
          >
            {k.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 text-sm">
        {draft.aggKind === 'group-by' && (
          <div className="flex items-center gap-2">
            <span className="text-fg-secondary">Group by</span>
            <MatchedSelect
              ariaLabel="Group by field"
              options={[...frame.catDims, ...frame.dateFields].map((d) => ({
                value: d,
                label: frame.labels[d] ?? d,
              }))}
              value={{
                value: draft.groupBy,
                label: frame.labels[draft.groupBy] ?? draft.groupBy,
              }}
              onChange={(v) => onChange({ ...draft, groupBy: v })}
            />
          </div>
        )}
        {draft.aggKind === 'pivot' && (
          <div className="flex items-center gap-2">
            <span className="text-fg-secondary">Rows</span>
            <MatchedSelect
              ariaLabel="Pivot rows field"
              options={[...frame.catDims, ...frame.dateFields].map((d) => ({
                value: d,
                label: frame.labels[d] ?? d,
              }))}
              value={{
                value: draft.groupBy,
                label: frame.labels[draft.groupBy] ?? draft.groupBy,
              }}
              onChange={(v) => onChange({ ...draft, groupBy: v })}
            />
            <span className="text-fg-secondary">Cols</span>
            <MatchedSelect
              ariaLabel="Pivot columns field"
              options={frame.catDims.map((d) => ({
                value: d,
                label: frame.labels[d] ?? d,
              }))}
              value={{
                value: draft.seriesBy,
                label: frame.labels[draft.seriesBy] ?? draft.seriesBy,
              }}
              onChange={(v) => onChange({ ...draft, seriesBy: v })}
            />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          {draft.metrics.map((m, i) => (
            <div key={i} className="flex items-center gap-1">
              <MetricFields
                agg={m.agg}
                field={m.field ?? frame.numFields[0] ?? ''}
                frame={frame}
                onChange={(agg, field) => {
                  const next = draft.metrics.map((x, j) =>
                    j === i ? { agg, field: agg !== 'count' ? field : null } : x
                  )
                  onChange({ ...draft, metrics: next })
                }}
              />
              {draft.metrics.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...draft,
                      metrics: draft.metrics.filter((_, j) => j !== i),
                    })
                  }
                  aria-label="Remove metric"
                  className="text-fg-tertiary hover:text-error-strong"
                >
                  <PiX size={12} />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() =>
              onChange({
                ...draft,
                metrics: [
                  ...draft.metrics,
                  { agg: 'count' as MetricAgg, field: null },
                ],
              })
            }
            className="flex w-fit items-center gap-1 text-xs text-fg-secondary hover:text-brand-active"
          >
            <PiPlus size={12} /> Add metric
          </button>
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          onClick={onApply}
          className="rounded-lg bg-brand-active px-3 py-1.5 text-xs font-medium text-white"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-fg-secondary hover:bg-surface-hover"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── PipelineStrip ─────────────────────────────────────────────────────────────

function PipelineStrip({
  steps,
  activeId,
  frame,
  onSelect,
  onAdd,
  onEdit,
  onRemoveAfter,
}: {
  steps: Step[]
  activeId: string
  frame: FrameDef
  onSelect: (id: string) => void
  onAdd: (afterId: string) => void
  onEdit: (afterId: string) => void
  onRemoveAfter: (afterIdx: number) => void
}) {
  return (
    <div className="flex items-center overflow-x-auto rounded-xl border border-line bg-surface p-3">
      {steps.map((step, i) => {
        const nextStep = steps[i + 1]
        return (
          <Fragment key={step.id}>
            <button
              type="button"
              onClick={() => onSelect(step.id)}
              className={cn(
                'shrink-0 whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                activeId === step.id
                  ? 'border-brand-active bg-brand-active/10 text-brand-active'
                  : 'border-line bg-surface text-fg-secondary hover:bg-surface-hover hover:text-fg'
              )}
            >
              {stepLabel(step, frame)}
            </button>

            {nextStep ? (
              <div className="flex shrink-0 items-center">
                <div className="h-px w-4 bg-line" />
                <div className="flex items-center gap-1 rounded-full border border-line bg-surface-sunken px-2.5 py-1 text-xs">
                  <span className="font-medium text-fg">
                    {connectorLabel(nextStep, frame)}
                  </span>
                  <div className="mx-0.5 h-3 w-px bg-line" />
                  <button
                    type="button"
                    onClick={() => onEdit(step.id)}
                    aria-label="Edit aggregation"
                    className="text-fg-tertiary hover:text-fg"
                  >
                    <PiPencilSimple size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRemoveAfter(i)}
                    aria-label="Remove aggregation"
                    className="text-fg-tertiary hover:text-error-strong"
                  >
                    <PiX size={11} />
                  </button>
                </div>
                <div className="h-px w-4 bg-line" />
              </div>
            ) : step.aggKind === null ? (
              // An aggregation is terminal: a pipeline allows at most one, and
              // nothing follows it. So "+ Add" only appears after a list step.
              <div className="flex shrink-0 items-center">
                <div className="h-px w-4 bg-line" />
                <button
                  type="button"
                  onClick={() => onAdd(step.id)}
                  className="flex items-center gap-1 rounded-full border border-dashed border-line px-2.5 py-1 text-xs text-fg-secondary hover:border-brand-active hover:text-brand-active"
                >
                  <PiPlus size={12} />
                  Add
                </button>
              </div>
            ) : null}
          </Fragment>
        )
      })}
    </div>
  )
}

// ── FilterPill ────────────────────────────────────────────────────────────────

const pillCls =
  'inline-flex h-8 items-center rounded-full border border-line bg-surface-sunken text-sm text-fg transition-colors hover:border-brand-active'

function FilterPill({
  label,
  editor,
  pendingOpen,
  onRemove,
}: {
  label: string
  editor: React.ReactNode
  pendingOpen: boolean
  onRemove: () => void
}) {
  const [open, setOpen] = useState(pendingOpen)
  return (
    <div className={pillCls}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex h-full items-center gap-1.5 rounded-l-full pl-3 pr-1.5"
          >
            <span className="max-w-[18rem] truncate">{label}</span>
            <PiCaretDown size={12} className="shrink-0 text-fg-secondary" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-0">
          {editor}
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label={`Remove ${label}`}
        onClick={onRemove}
        className="inline-flex h-full items-center rounded-r-full pl-0.5 pr-2 text-fg-secondary hover:text-error"
      >
        <PiX size={12} />
      </button>
    </div>
  )
}

const ghostCls =
  'inline-flex h-8 items-center gap-1 rounded-md px-2 text-sm text-fg-secondary transition-colors hover:bg-surface-hover hover:text-fg'

function SortableColItem({
  id,
  label,
  onRemove,
}: {
  id: string
  label: string
  onRemove: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className="flex items-center gap-1.5 rounded border border-line bg-surface px-2 py-1 text-xs"
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab text-fg-subtle"
      >
        <PiDotsSixVertical size={13} />
      </span>
      <span className="flex-1 text-fg">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="text-fg-subtle hover:text-fg"
        aria-label={`Remove ${label}`}
      >
        <PiX size={11} />
      </button>
    </div>
  )
}

function StepDetail({
  step,
  frame,
  sources,
  inherited,
  inheritedFrom,
  onChange,
  onSave,
  onSaveAs,
}: {
  step: Step
  frame: FrameDef
  sources: SourceId[]
  inherited: Filter[]
  inheritedFrom: string[]
  onChange: (next: Step) => void
  onSave: () => void
  onSaveAs?: () => void
}) {
  const { kpis, bump } = useKpis()
  const [savingKpi, setSavingKpi] = useState(false)
  const [kpiName, setKpiName] = useState('')
  const [showFilterBar, setShowFilterBar] = useState(false)
  const [pendingOpenKey, setPendingOpenKey] = useState<string | null>(null)
  const [sortOpen, setSortOpen] = useState(false)
  const [vizView, setVizView] = useState<ReportView | null>(null)

  const colSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const config = toConfig(step, frame, sources, inherited)
  const result = runQuery(config, frame)
  const activeView = resolveView(config, result, vizView)
  const totalRows =
    result.kind === 'list'
      ? result.rows.length
      : step.aggKind === 'summary'
        ? 1
        : result.groups.length
  const previewResult: typeof result =
    result.kind === 'list'
      ? { ...result, rows: result.rows.slice(0, 10) }
      : { ...result, groups: result.groups.slice(0, 10) }
  const previewCount = Math.min(10, totalRows)

  const setPanel = (p: Panel) =>
    onChange({ ...step, panel: step.panel === p ? null : p })

  const isAgg = !!step.aggKind
  const vc = asVc(step.filters)
  const applyFilters = (nextVc: ViewConfig) =>
    onChange({ ...step, filters: nextVc.filters })
  const fieldValues = (field: string) =>
    frame.fields.find((f) => f.name === field)?.values?.map(String) ?? []
  const fieldLabel = (field: string) => frame.labels[field] ?? field

  const pills: Pill[] = [
    ...inFilterFields(vc).map((f): Pill => ({ kind: 'cat', field: f })),
    ...numericFilterFields(vc).map((f): Pill => ({ kind: 'num', field: f })),
    ...dateFilterFields(vc).map((f): Pill => ({ kind: 'date', field: f })),
  ]
  const taken = new Set(
    step.filters.flatMap((f) => ('field' in f ? [f.field] : []))
  )
  const catDims: string[] = isAgg
    ? step.aggKind === 'group-by' && step.groupBy
      ? [step.groupBy]
      : step.aggKind === 'pivot' && step.groupBy && step.seriesBy
        ? [step.groupBy, step.seriesBy]
        : []
    : frame.catDims
  // After aggregation, only output dimensions (groupBy/seriesBy, all
  // categorical) are filterable — raw upstream numeric/date columns don't exist
  // in the result, so filtering on them is a no-op. Gate them like catDims.
  const unusedCat = catDims.filter((f) => !taken.has(f))
  const unusedNum = isAgg ? [] : frame.numFields.filter((f) => !taken.has(f))
  const unusedDate = isAgg ? [] : frame.dateFields.filter((f) => !taken.has(f))
  const canAdd =
    unusedCat.length > 0 || unusedNum.length > 0 || unusedDate.length > 0
  const sortCols = isAgg ? (step.groupBy ? [step.groupBy] : []) : step.columns // the columns actually shown, not the frame defaults

  const pillLabel = (p: Pill): string => {
    if (p.kind === 'cat') {
      const vals = inFilterValues(vc, p.field)
      const allCount = fieldValues(p.field).length
      const text =
        vals.length === 0
          ? 'any'
          : vals.length === allCount
            ? 'all'
            : vals.length <= 2
              ? vals.join(', ')
              : `${vals.length} selected`
      return `${fieldLabel(p.field)}: ${text}`
    }
    if (p.kind === 'num') {
      const cond = numericFilterOf(vc, p.field)
      const sym = cond?.op === 'lte' ? '≤' : '≥'
      return `${fieldLabel(p.field)} ${sym} ${cond?.value ?? 0}`
    }
    const cond = dateFilterOf(vc, p.field)
    if (cond?.op === 'within_days')
      return `${fieldLabel(p.field)} ≤ ${cond.value}d`
    const sym = cond?.op === 'date_before' ? '≤' : '≥'
    return `${fieldLabel(p.field)} ${sym} ${cond?.value ?? ''}`
  }

  const pillEditor = (p: Pill): React.ReactNode => {
    if (p.kind === 'cat') {
      return (
        <>
          <MultiSelectList
            label={fieldLabel(p.field)}
            idPrefix={`pill-${p.field}`}
            options={fieldValues(p.field).map((v) => ({ value: v, label: v }))}
            selected={inFilterValues(vc, p.field)}
            onChange={(vals) => applyFilters(setInFilter(vc, p.field, vals))}
          />
          <div className="border-t border-line p-1">
            <button
              type="button"
              onClick={() => {
                applyFilters(removeInFilter(vc, p.field))
              }}
              className="flex w-full rounded px-2 py-1.5 text-left text-sm text-fg-secondary hover:bg-surface-hover hover:text-error"
            >
              Remove filter
            </button>
          </div>
        </>
      )
    }
    if (p.kind === 'num') {
      const cond = numericFilterOf(vc, p.field)
      return (
        <div className="p-3">
          <NumericFilterRow
            label={fieldLabel(p.field)}
            ariaLabel={fieldLabel(p.field)}
            op={cond?.op ?? 'gte'}
            value={cond?.value ?? 0}
            onChange={(op, value) =>
              applyFilters(setNumericFilter(vc, p.field, op, value))
            }
            onRemove={() => {
              applyFilters(removeNumericFilter(vc, p.field))
            }}
          />
        </div>
      )
    }
    const cond = dateFilterOf(vc, p.field)
    return (
      <div className="p-3">
        <DateCompareFilterRow
          label={fieldLabel(p.field)}
          ariaLabel={fieldLabel(p.field)}
          op={(cond?.op ?? 'date_after') as DateOp}
          value={cond?.value ?? ''}
          onChange={(op, value) =>
            applyFilters(setDateFilter(vc, p.field, op, value))
          }
          onRemove={() => {
            applyFilters(removeDateFilter(vc, p.field))
          }}
        />
      </div>
    )
  }

  const inheritedFields = Array.from(
    new Set(inherited.flatMap((f) => ('field' in f ? [f.field] : [])))
  )

  return (
    <div className="rounded-xl border border-line bg-surface shadow-sm">
      {/* header */}
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <span className="font-semibold text-fg">{stepLabel(step, frame)}</span>
        <div className="flex items-center gap-2">
          {
            // Summary = single-row rollup: filter and sort are meaningless.
            (step.aggKind === 'summary'
              ? []
              : ([
                  {
                    label: 'Filter',
                    icon: <PiFunnel size={15} />,
                    dot: pills.length > 0,
                    active: showFilterBar,
                    // Toggle the conditions bar; on close, clear the one-shot
                    // pill key and any open sort popover so a reopen is clean.
                    onClick: () => {
                      if (showFilterBar) {
                        setPendingOpenKey(null)
                        setSortOpen(false)
                      }
                      setShowFilterBar((v) => !v)
                    },
                  },
                  {
                    label: 'Sort',
                    icon: <PiArrowsDownUp size={15} />,
                    dot: !!step.sort,
                    active: sortOpen,
                    // The sort editor lives inside the bar — reveal the bar and
                    // toggle the sort popover directly (not just the bar).
                    onClick: () => {
                      setShowFilterBar(true)
                      setSortOpen((v) => !v)
                    },
                  },
                ] as {
                  label: string
                  icon: React.ReactNode
                  dot: boolean
                  active: boolean
                  onClick: () => void
                }[])
            ).map(({ label, icon, dot, active, onClick }) => (
              <button
                key={label}
                type="button"
                aria-label={label}
                aria-pressed={active}
                onClick={onClick}
                className={cn(
                  'relative inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-colors',
                  active
                    ? 'border-brand-active text-brand-active'
                    : 'border-line text-fg-secondary hover:text-fg'
                )}
              >
                {icon}
                {dot && (
                  <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-warning ring-2 ring-surface" />
                )}
              </button>
            ))
          }
          {step.aggKind !== 'summary' && (
            <>
              <div className="h-4 w-px bg-line" />
              <ToolbarIcon
                icon={<PiRows size={15} />}
                label={`Limit: ${step.limit === 0 ? 'All' : step.limit}`}
                active={step.panel === 'limit'}
                onClick={() => setPanel('limit')}
              />
            </>
          )}
          {!isAgg && (
            <ToolbarIcon
              icon={<PiColumns size={15} />}
              label={`${step.columns.length} cols`}
              active={step.panel === 'columns'}
              onClick={() => setPanel('columns')}
            />
          )}
          {!isAgg && (
            <ToolbarIcon
              icon={<span className="font-medium">Σ</span>}
              label={step.summaryFn ? `Summary: ${step.summaryFn}` : 'Summary'}
              active={step.panel === 'summary-row'}
              badge={step.summaryFn ? 1 : undefined}
              onClick={() => setPanel('summary-row')}
            />
          )}
          {/* Grouped tables can append a grand-total footer (the rollup the
              engine already computes for the pivot). No sum/avg choice — the
              total uses each metric's own agg — so this is a plain on/off
              toggle, reusing `summaryFn` ('sum' = on) rather than a new field. */}
          {step.aggKind === 'group-by' && (
            <ToolbarIcon
              icon={<span className="font-medium">Σ</span>}
              label={step.summaryFn ? 'Total: on' : 'Total'}
              active={false}
              badge={step.summaryFn ? 1 : undefined}
              onClick={() =>
                onChange({ ...step, summaryFn: step.summaryFn ? null : 'sum' })
              }
            />
          )}
          {isAgg && (
            <ToolbarIcon
              icon={<PiTarget size={15} />}
              label="Goal"
              active={step.panel === 'goal'}
              badge={step.goal !== null ? 1 : undefined}
              onClick={() => setPanel('goal')}
            />
          )}
          <div className="h-4 w-px bg-line" />
          <button
            type="button"
            onClick={onSave}
            className="flex items-center gap-1 rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-fg-secondary hover:bg-surface-hover hover:text-fg"
          >
            <PiBookmarkSimple size={13} />
            Save
          </button>
          {onSaveAs && (
            <button
              type="button"
              onClick={onSaveAs}
              className="flex items-center gap-1 rounded-lg border border-line bg-surface px-2.5 py-1 text-xs font-medium text-fg-secondary hover:bg-surface-hover hover:text-fg"
            >
              <PiCopy size={13} />
              Save as new
            </button>
          )}
        </div>
      </div>

      {/* Filter cascade cue: filters defined on upstream steps narrow this
          step's data. Read-only here — edit them on their own step. */}
      {inherited.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-line bg-surface-sunken px-4 py-2 text-xs text-fg-tertiary">
          <PiFunnel size={13} />
          <span className="font-medium">
            Inherited from {inheritedFrom.join(', ') || 'upstream'}
          </span>
          {inheritedFields.length > 0 && (
            <span>
              · {inheritedFields.map((f) => fieldLabel(f)).join(', ')}
            </span>
          )}
        </div>
      )}

      {/* Aggregation definition (group-by/pivot dims + measures) is edited on
          the Edit-aggregation screen (AggForm), not inline here. */}

      {/* filter + sort bar */}
      {showFilterBar && (
        <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2">
          {pills.map((p) => {
            const key = pillKey(p)
            return (
              <FilterPill
                key={key}
                label={pillLabel(p)}
                editor={pillEditor(p)}
                pendingOpen={pendingOpenKey === key}
                onRemove={() => {
                  if (p.kind === 'cat')
                    applyFilters(removeInFilter(vc, p.field))
                  else if (p.kind === 'num')
                    applyFilters(removeNumericFilter(vc, p.field))
                  else applyFilters(removeDateFilter(vc, p.field))
                }}
              />
            )
          })}

          {canAdd && (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className={ghostCls}>
                  <PiPlus size={14} /> Filter
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-56 p-1"
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                {unusedCat.length > 0 && (
                  <MenuSection label="Categories">
                    {unusedCat.map((f) => (
                      <PopoverClose key={f} asChild>
                        <MenuItem
                          label={fieldLabel(f)}
                          onClick={() => {
                            applyFilters(setInFilter(vc, f, []))
                            setPendingOpenKey(
                              pillKey({ kind: 'cat', field: f })
                            )
                          }}
                        />
                      </PopoverClose>
                    ))}
                  </MenuSection>
                )}
                {unusedNum.length > 0 && (
                  <MenuSection label="Numbers">
                    {unusedNum.map((f) => (
                      <PopoverClose key={f} asChild>
                        <MenuItem
                          label={fieldLabel(f)}
                          onClick={() => {
                            applyFilters(setNumericFilter(vc, f, 'gte', 0))
                            setPendingOpenKey(
                              pillKey({ kind: 'num', field: f })
                            )
                          }}
                        />
                      </PopoverClose>
                    ))}
                  </MenuSection>
                )}
                {unusedDate.length > 0 && (
                  <MenuSection label="Dates">
                    {unusedDate.map((f) => (
                      <PopoverClose key={f} asChild>
                        <MenuItem
                          label={fieldLabel(f)}
                          onClick={() => {
                            applyFilters(
                              setDateFilter(
                                vc,
                                f,
                                'date_after',
                                `${new Date().getFullYear()}-01-01`
                              )
                            )
                            setPendingOpenKey(
                              pillKey({ kind: 'date', field: f })
                            )
                          }}
                        />
                      </PopoverClose>
                    ))}
                  </MenuSection>
                )}
              </PopoverContent>
            </Popover>
          )}

          {step.aggKind !== 'summary' && (
            <Popover open={sortOpen} onOpenChange={setSortOpen}>
              <PopoverTrigger asChild>
                <button type="button" className={ghostCls}>
                  {step.sort ? (
                    <span className="text-fg">
                      Sort: {fieldLabel(step.sort.by)}{' '}
                      {step.sort.dir === 'asc' ? '↑' : '↓'}
                    </span>
                  ) : (
                    'Sort'
                  )}
                  <PiCaretDown
                    size={12}
                    className="shrink-0 text-fg-secondary"
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="flex w-56 flex-col gap-2 p-3"
              >
                <div className="flex flex-col">
                  <span className="mb-1 text-xs text-fg-secondary">
                    Sort by
                  </span>
                  <MatchedSelect
                    ariaLabel="Sort field"
                    options={[
                      { value: '', label: 'Default order' },
                      ...sortCols.map((c) => ({
                        value: c,
                        label: fieldLabel(c),
                      })),
                    ]}
                    value={{
                      value: step.sort?.by ?? '',
                      label: step.sort
                        ? fieldLabel(step.sort.by)
                        : 'Default order',
                    }}
                    onChange={(v) =>
                      onChange({
                        ...step,
                        sort: v
                          ? { by: v, dir: step.sort?.dir ?? 'desc' }
                          : null,
                      })
                    }
                  />
                </div>
                {step.sort && (
                  <div className="flex flex-col">
                    <span className="mb-1 text-xs text-fg-secondary">
                      Order
                    </span>
                    <MatchedSelect
                      ariaLabel="Sort order"
                      options={[
                        { value: 'desc', label: 'High → low' },
                        { value: 'asc', label: 'Low → high' },
                      ]}
                      value={{
                        value: step.sort.dir,
                        label:
                          step.sort.dir === 'asc' ? 'Low → high' : 'High → low',
                      }}
                      onChange={(dir) =>
                        onChange({
                          ...step,
                          sort: { ...step.sort!, dir: dir as 'asc' | 'desc' },
                        })
                      }
                    />
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}

      {step.panel === 'limit' && (
        <div className="border-b border-line bg-surface-sunken px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-fg-secondary">Limit</span>
            <div className="flex overflow-hidden rounded-lg border border-line">
              {[10, 25, 50, 0].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => onChange({ ...step, limit: n })}
                  className={cn(
                    'px-2.5 py-1 text-xs',
                    step.limit === n
                      ? 'bg-brand-active text-white'
                      : 'bg-surface text-fg-secondary hover:bg-surface-hover'
                  )}
                >
                  {n === 0 ? 'All' : n}
                </button>
              ))}
            </div>
            <span className="text-sm text-fg-secondary">rows</span>
          </div>
        </div>
      )}

      {step.panel === 'columns' && !isAgg && (
        <div className="border-b border-line bg-surface-sunken px-4 py-3 space-y-3">
          <div>
            <p className="mb-1.5 text-xs font-medium text-fg-secondary">
              Selected columns — drag to reorder
            </p>
            <DndContext
              sensors={colSensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={(e: DragEndEvent) => {
                const { active, over } = e
                if (over && active.id !== over.id) {
                  const oldIdx = step.columns.indexOf(active.id as string)
                  const newIdx = step.columns.indexOf(over.id as string)
                  onChange({
                    ...step,
                    columns: arrayMove(step.columns, oldIdx, newIdx),
                  })
                }
              }}
            >
              <SortableContext
                items={step.columns}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-1">
                  {step.columns.map((col) => (
                    <SortableColItem
                      key={col}
                      id={col}
                      label={frame.labels[col] ?? col}
                      onRemove={() => {
                        const next = step.columns.filter((c) => c !== col)
                        if (next.length > 0)
                          onChange({ ...step, columns: next })
                      }}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
          {frame.columns.filter((c) => !step.columns.includes(c)).length >
            0 && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-fg-secondary">
                Add columns
              </p>
              <div className="flex flex-wrap gap-1.5">
                {frame.columns
                  .filter((c) => !step.columns.includes(c))
                  .map((col) => (
                    <button
                      key={col}
                      type="button"
                      onClick={() =>
                        onChange({ ...step, columns: [...step.columns, col] })
                      }
                      className="rounded-full border border-line bg-surface px-2.5 py-0.5 text-xs font-medium text-fg-secondary hover:bg-surface-hover hover:text-fg transition-colors"
                    >
                      + {frame.labels[col] ?? col}
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step.panel === 'summary-row' && !isAgg && (
        <div className="border-b border-line bg-surface-sunken px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-fg-secondary">Summary row</span>
            <div className="flex overflow-hidden rounded-lg border border-line">
              {(
                [
                  { label: 'Off', value: null },
                  { label: 'Sum', value: 'sum' },
                  { label: 'Avg', value: 'avg' },
                ] as { label: string; value: 'sum' | 'avg' | null }[]
              ).map(({ label, value }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => onChange({ ...step, summaryFn: value })}
                  className={cn(
                    'px-2.5 py-1 text-xs',
                    step.summaryFn === value
                      ? 'bg-brand-active text-white'
                      : 'bg-surface text-fg-secondary hover:bg-surface-hover'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step.panel === 'goal' && isAgg && (
        <div className="border-b border-line bg-surface-sunken px-4 py-3">
          {step.goal === null ? (
            <button
              type="button"
              onClick={() =>
                onChange({ ...step, goal: { value: 0, dir: 'above' } })
              }
              className="text-sm text-brand-active hover:underline"
            >
              + Add goal
            </button>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="w-20 shrink-0 text-sm text-fg-secondary">
                  Source
                </span>
                <select
                  value={step.goal.kpiId ?? ''}
                  onChange={(e) => {
                    const kpi: Kpi | undefined = kpis.find(
                      (k) => k.id === e.target.value
                    )
                    if (kpi) {
                      onChange({
                        ...step,
                        goal: {
                          value: kpi.value,
                          kpiId: kpi.id,
                          dir: kpi.dir ?? step.goal!.dir,
                        },
                      })
                    } else {
                      onChange({
                        ...step,
                        goal: { ...step.goal!, kpiId: undefined },
                      })
                    }
                  }}
                  className="rounded-lg border border-line bg-surface px-2 py-1 text-sm text-fg"
                >
                  <option value="">Custom value</option>
                  {kpis.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-20 shrink-0 text-sm text-fg-secondary">
                  Target
                </span>
                <input
                  aria-label="Goal target value"
                  type="number"
                  disabled={!!step.goal.kpiId}
                  value={String(
                    // ponytail: String() so React diffs the DOM value as a string —
                    // a number value lets React's loose != skip rewriting "0100" (== 100)
                    step.goal.kpiId
                      ? (kpis.find((k) => k.id === step.goal!.kpiId)?.value ??
                          step.goal.value)
                      : step.goal.value
                  )}
                  onChange={(e) =>
                    onChange({
                      ...step,
                      goal: { ...step.goal!, value: Number(e.target.value) },
                    })
                  }
                  className="h-8 w-32 rounded-lg border border-line bg-surface px-2 text-sm text-fg disabled:opacity-60"
                />
                <select
                  value={step.goal.dir}
                  onChange={(e) =>
                    onChange({
                      ...step,
                      goal: {
                        ...step.goal!,
                        dir: e.target.value as 'above' | 'below',
                      },
                    })
                  }
                  className="rounded-lg border border-line bg-surface px-2 py-1 text-sm text-fg"
                >
                  <option value="above">Goal ≥ target</option>
                  <option value="below">Goal ≤ target</option>
                </select>
              </div>
              {!step.goal.kpiId &&
                (savingKpi ? (
                  <div className="flex items-center gap-2">
                    <input
                      aria-label="Goal name"
                      type="text"
                      placeholder="Goal name…"
                      value={kpiName}
                      onChange={(e) => setKpiName(e.target.value)}
                      className="h-8 rounded-lg border border-line bg-surface px-2 text-sm text-fg outline-none focus:border-line-focus"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!kpiName.trim()) return
                        const kpi = saveKpi({
                          name: kpiName.trim(),
                          value: step.goal!.value,
                          dir: step.goal!.dir,
                        })
                        onChange({
                          ...step,
                          goal: { ...step.goal!, kpiId: kpi.id },
                        })
                        bump()
                        setSavingKpi(false)
                        setKpiName('')
                      }}
                      className="text-sm text-brand-active hover:underline"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setSavingKpi(false)}
                      className="text-sm text-fg-secondary hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSavingKpi(true)}
                    className="w-fit text-sm text-fg-secondary hover:text-fg hover:underline"
                  >
                    Save as KPI
                  </button>
                ))}
              <button
                type="button"
                onClick={() => onChange({ ...step, goal: null })}
                className="w-fit text-sm text-error-strong hover:underline"
              >
                Remove goal
              </button>
            </div>
          )}
        </div>
      )}

      {/* View switcher sits above the preview, matching ReportViewer and the
          report detail page (every other surface puts it in a top toolbar).
          Renders null with ≤1 available view; skip the bar so a single-view
          report has no empty header strip. */}
      {availableViews(config, result).length > 1 && (
        <div className="flex border-b border-line px-4 py-2">
          <div className="ml-auto">
            <ViewSwitcher
              config={config}
              result={result}
              view={activeView}
              onChange={setVizView}
            />
          </div>
        </div>
      )}
      {/* List tables and charts fill a fixed height (they scroll internally /
          need a definite height to size into). Aggregate tables render at
          `height="auto"` — a fixed height clips their handful of rows, so let
          them size to content (the page scrolls) and only scroll horizontally
          for a wide pivot. */}
      <div
        className={cn(
          'flex flex-col',
          activeView === 'table' && result.kind === 'aggregate'
            ? 'overflow-x-auto'
            : 'h-[calc(100vh-26rem)] overflow-auto'
        )}
      >
        {activeView === 'metric' ? (
          <div className="max-w-sm p-4">
            <MetricView config={config} result={result} />
          </div>
        ) : activeView === 'table' ? (
          // Flush inside StepDetail's card (no inner card, no padding): the table
          // fills the space and the preview count is a flush border-t bar — same
          // layout as the detail view's "Total … pagination" bar.
          <>
            <div className="flex min-h-0 flex-1 flex-col">
              <ReportTable
                config={config}
                result={previewResult}
                summaryResult={result}
                fill
              />
            </div>
            <div className="shrink-0 border-t border-line px-4 py-2 text-xs text-fg-secondary">
              Previewing {previewCount.toLocaleString()} /{' '}
              {totalRows.toLocaleString()} rows
            </div>
          </>
        ) : activeView === 'ranked' ? (
          <div className="p-4">
            <RankedListView config={config} result={result} />
          </div>
        ) : (
          <div className="min-h-0 flex-1 p-4">
            <ReportChart
              bare
              fill
              chartType={activeView as ReportChartType}
              config={{ ...config, chart: activeView as ReportChartType }}
              result={result}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ── source icons ─────────────────────────────────────────────────────────────

const SOURCE_ICONS: Record<SourceId, React.ReactNode> = {
  deals: <PiHandshake size={20} />,
  opportunities: <PiBuildings size={20} />,
  activity: <PiListDashes size={20} />,
  contacts: <PiUsers size={20} />,
}

// ── SourceSelectScreen ────────────────────────────────────────────────────────

function SourceSelectScreen({
  selected,
  onToggle,
  onConfirm,
}: {
  selected: SourceId[]
  onToggle: (id: SourceId) => void
  onConfirm: () => void
}) {
  const frame = frameForSources(selected)

  const previewConfig: ViewConfig | null = frame
    ? {
        id: 'src-preview',
        name: 'preview',
        description: '',
        kind: 'list',
        frame: 'deal',
        filters: [],
        columns: frame.defaultColumns,
        chart: 'table-only',
        knobs: [],
        limit: 0,
      }
    : null
  const previewResult =
    previewConfig && frame ? runQuery(previewConfig, frame) : null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-fg-tertiary">
            Step 1 of 2
          </p>
          <h2 className="mt-1 text-xl font-bold text-fg">Choose your data</h2>
          <p className="mt-1 text-sm text-fg-secondary">
            Select one or more sources. Where a join key exists, tables are
            inner-joined automatically.
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <button
            type="button"
            disabled={!frame}
            onClick={onConfirm}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-active px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            Start building
            <PiArrowRight size={16} />
          </button>
          {!frame && (
            <p className="text-xs text-fg-secondary">
              Add Deals as the anchor.
            </p>
          )}
        </div>
      </div>

      {/* source cards */}
      <div className="grid grid-cols-3 gap-4">
        {SOURCES.map((src) => {
          const isSelected = selected.includes(src.id)
          const wouldBreak =
            !isSelected && frameForSources([...selected, src.id]) === null
          // Explain the only two ways a source breaks the join (see frameForSources).
          const breakReason = !wouldBreak
            ? undefined
            : (src.id === 'activity' && selected.includes('contacts')) ||
                (src.id === 'contacts' && selected.includes('activity'))
              ? "Contacts and Deal Activity can't share one report — they fan out from a deal on different keys, so joining both produces meaningless contact×activity rows. Use two reports."
              : 'Deal Activity needs Deals as the anchor to join.'
          return (
            <button
              key={src.id}
              type="button"
              disabled={wouldBreak}
              title={breakReason}
              onClick={() => onToggle(src.id)}
              className={cn(
                'relative flex flex-col gap-2 rounded-2xl border-2 p-4 text-left transition-all',
                isSelected
                  ? 'border-brand-active bg-brand-active/5 shadow-sm'
                  : wouldBreak
                    ? 'cursor-not-allowed border-line bg-surface opacity-40'
                    : 'border-line bg-surface hover:border-brand-active/40 hover:bg-surface-hover'
              )}
            >
              {/* check indicator */}
              <div
                className={cn(
                  'absolute right-4 top-4 flex size-5 items-center justify-center rounded-full border-2 transition-colors',
                  isSelected
                    ? 'border-brand-active bg-brand-active text-white'
                    : 'border-line bg-surface'
                )}
              >
                {isSelected && (
                  <PiX size={10} style={{ transform: 'rotate(45deg)' }} />
                )}
              </div>

              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'transition-colors',
                    isSelected ? 'text-brand-active' : 'text-fg-secondary'
                  )}
                >
                  {SOURCE_ICONS[src.id]}
                </span>
                <p
                  className={cn(
                    'font-semibold',
                    isSelected ? 'text-brand-active' : 'text-fg'
                  )}
                >
                  {src.label}
                </p>
              </div>

              <p className="text-xs text-fg-secondary">{src.description}</p>

              <p className="text-xs font-medium text-fg-tertiary">
                {src.count.toLocaleString()} rows
              </p>
            </button>
          )
        })}
      </div>

      {/* join preview */}
      {selected.length > 1 && frame && (
        <div className="rounded-xl border border-line bg-surface-sunken px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary">
            Join preview
          </p>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            {selected.map((sid, i) => (
              <Fragment key={sid}>
                <span className="rounded-lg border border-brand-active/30 bg-brand-active/10 px-3 py-1.5 text-sm font-medium text-brand-active">
                  {SOURCES.find((s) => s.id === sid)?.label}
                </span>
                {i < selected.length - 1 && (
                  <div className="h-px w-8 border-t border-dashed border-fg-tertiary" />
                )}
              </Fragment>
            ))}
          </div>
          <p className="mt-2 text-xs text-fg-secondary">
            {frame.records.length.toLocaleString()} rows after inner join
          </p>
        </div>
      )}

      {/* data preview */}
      {previewConfig && previewResult && previewResult.kind === 'list' && (
        <div className="overflow-hidden rounded-xl border border-line bg-surface">
          <p className="border-b border-line px-5 py-3 text-xs font-semibold uppercase tracking-wide text-fg-tertiary">
            Data preview
          </p>
          <div className="p-3">
            <ReportTable
              config={previewConfig}
              result={{
                ...previewResult,
                rows: previewResult.rows.slice(0, 3),
              }}
              autoHeight
            />
          </div>
          <p className="px-4 pb-3 text-xs text-fg-tertiary">
            Showing {Math.min(3, previewResult.rows.length).toLocaleString()} of{' '}
            {previewResult.rows.length.toLocaleString()} rows
          </p>
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ReportBuilderPage({
  editingId,
}: {
  editingId?: string
}) {
  const navigate = useNavigate()
  const editingView = editingId ? getSavedView(editingId) : undefined

  const [screen, setScreen] = useState<'select' | 'build'>(() =>
    editingView ? 'build' : 'select'
  )
  // Prefer the persisted source set; fall back to the collapsed frameId for
  // reports saved before `sources` existed. (frameId alone can't reconstruct a
  // multi-source join — see frameForConfig / the join-drop fix.)
  const sourcesForEdit = (v: typeof editingView): SourceId[] =>
    v?.sources ?? sourcesFromFrameId(v?.frame ?? 'deal')

  const [selectedSources, setSelectedSources] = useState<SourceId[]>(() =>
    editingView ? sourcesForEdit(editingView) : ['deals']
  )

  const frame = frameForSources(selectedSources)

  const [steps, setSteps] = useState<Step[]>(() => {
    if (editingView) {
      const sources = sourcesForEdit(editingView)
      const f = frameForSources(sources)
      if (f) return [stepFromConfig(editingView, f)]
    }
    return [makeStep(frameForSources(['deals'])!)]
  })
  const [adder, setAdder] = useState<Adder | null>(null)
  const [activeId, setActiveId] = useState<string>(steps[0].id)
  const [saveDialogStep, setSaveDialogStep] = useState<Step | null>(null)

  const activeStep =
    steps.find((s) => s.id === activeId) ?? steps[steps.length - 1]

  // A step inherits the filters of every step before it.
  const inheritedFor = (step: Step) =>
    upstreamFilters(
      steps,
      steps.findIndex((s) => s.id === step.id)
    )

  // Labels of the upstream steps that actually contribute a filter — names the
  // *source* of the cascade (e.g. "All Deals"), not the filtered field.
  const inheritedFromFor = (step: Step): string[] => {
    if (!frame) return []
    const i = steps.findIndex((s) => s.id === step.id)
    return steps
      .slice(0, Math.max(0, i))
      .filter((s) => s.filters.length > 0)
      .map((s) => stepLabel(s, frame))
  }

  const toggleSource = (id: SourceId) => {
    const next = selectedSources.includes(id)
      ? selectedSources.filter((s) => s !== id)
      : [...selectedSources, id]
    // Only block ADDING a source that breaks the join. Removing is always allowed
    // — including down to zero (the picker just shows no preview, Start disabled).
    if (next.length > selectedSources.length && !frameForSources(next)) return
    setSelectedSources(next)
  }

  const confirmSources = () => {
    if (!frame) return
    const first = makeStep(frame)
    setSteps([first])
    setActiveId(first.id)
    setAdder(null)
    setScreen('build')
  }

  const updateStep = (id: string, next: Step) =>
    setSteps((ss) => ss.map((s) => (s.id === id ? next : s)))

  const openAdder = (afterId: string) => {
    if (!frame) return
    const afterIdx = steps.findIndex((s) => s.id === afterId)
    const nx = steps[afterIdx + 1]
    setAdder({
      afterId,
      aggKind: nx?.aggKind ?? 'group-by',
      groupBy: nx?.groupBy ?? frame.catDims[0] ?? '',
      seriesBy: nx?.seriesBy ?? frame.catDims[1] ?? frame.catDims[0] ?? '',
      metrics: nx?.metrics ?? [{ agg: 'count', field: null }],
    })
  }

  const applyAdder = () => {
    if (!adder || !frame) return
    const afterIdx = steps.findIndex((s) => s.id === adder.afterId)
    const existingNext = steps[afterIdx + 1]
    const patch: Partial<Step> = {
      aggKind: adder.aggKind,
      groupBy: adder.aggKind !== 'summary' ? adder.groupBy : null,
      seriesBy: adder.aggKind === 'pivot' ? adder.seriesBy : null,
      metrics: adder.metrics.map((m) => ({
        agg: m.agg,
        field: m.agg !== 'count' ? m.field : null,
      })),
    }
    if (existingNext?.aggKind) {
      setSteps((ss) =>
        ss.map((s) => (s.id === existingNext.id ? { ...s, ...patch } : s))
      )
      setActiveId(existingNext.id)
    } else {
      const newStep = { ...makeStep(frame), ...patch }
      setSteps((ss) => {
        const idx = ss.findIndex((s) => s.id === adder.afterId)
        const next = [...ss]
        next.splice(idx + 1, next.length, newStep)
        return next
      })
      setActiveId(newStep.id)
    }
    setAdder(null)
  }

  const removeAfter = (afterIdx: number) => {
    const removedIds = new Set(steps.slice(afterIdx + 1).map((s) => s.id))
    setSteps((ss) => ss.slice(0, afterIdx + 1))
    if (removedIds.has(activeId)) setActiveId(steps[afterIdx].id)
  }

  const handleSave = (step: Step) => setSaveDialogStep(step)

  const handleSaveInPlace = (step: Step) => {
    if (!frame || !editingId || !editingView) return
    const config = toConfig(step, frame, selectedSources, inheritedFor(step))
    updateSavedView(
      editingId,
      { ...config, description: editingView.description ?? '' },
      editingView.name
    )
    toast.success(`Saved "${editingView.name}"`)
    navigate(`/reporting/${editingId}`)
  }

  const handleConfirmSave = ({ name, description }: SaveResult) => {
    if (!frame || !saveDialogStep) return
    const config = toConfig(
      saveDialogStep,
      frame,
      selectedSources,
      inheritedFor(saveDialogStep)
    )
    saveView({ ...config, description }, name)
    toast.success(`Saved "${name}"`)
    navigate('/reporting')
  }

  return (
    <div className={cn(REPORTING_PAGE_SHELL, 'gap-6')}>
      {/* nav */}
      <div className="flex items-center gap-3">
        {screen === 'build' && !editingId ? (
          <button
            type="button"
            onClick={() => setScreen('select')}
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
          >
            <PiArrowLeft size={14} />
            Change sources
          </button>
        ) : (
          <Link
            to={editingId ? `/reporting/${editingId}` : '/reporting'}
            className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
          >
            <PiArrowLeft size={14} /> {editingId ? 'Cancel' : 'Reports'}
          </Link>
        )}
        {screen === 'build' && frame && (
          <span className="text-sm text-fg-tertiary">
            {frame.label}
            {frame.records.length > 0 &&
              ` · ${frame.records.length.toLocaleString()} rows`}
          </span>
        )}
      </div>

      {screen === 'select' ? (
        <SourceSelectScreen
          selected={selectedSources}
          onToggle={toggleSource}
          onConfirm={confirmSources}
        />
      ) : frame ? (
        <div className="flex flex-col gap-6">
          <PipelineStrip
            steps={steps}
            activeId={activeId}
            frame={frame}
            onSelect={setActiveId}
            onAdd={openAdder}
            onEdit={openAdder}
            onRemoveAfter={removeAfter}
          />
          {adder ? (
            <AggForm
              draft={adder}
              frame={frame}
              onChange={setAdder}
              onApply={applyAdder}
              onCancel={() => setAdder(null)}
            />
          ) : (
            <StepDetail
              key={activeStep.id}
              step={activeStep}
              frame={frame}
              sources={selectedSources}
              inherited={inheritedFor(activeStep)}
              inheritedFrom={inheritedFromFor(activeStep)}
              onChange={(next) => updateStep(activeStep.id, next)}
              onSave={
                editingId
                  ? () => handleSaveInPlace(activeStep)
                  : () => handleSave(activeStep)
              }
              onSaveAs={editingId ? () => handleSave(activeStep) : undefined}
            />
          )}
        </div>
      ) : null}

      <SaveDialog
        open={saveDialogStep !== null}
        onClose={() => setSaveDialogStep(null)}
        kind="report"
        defaultName={
          saveDialogStep && frame ? stepLabel(saveDialogStep, frame) : ''
        }
        titleOverride={editingId ? 'Save as new report' : undefined}
        acceptTextOverride={editingId ? 'Save as new' : undefined}
        onSaveNew={handleConfirmSave}
      />
    </div>
  )
}
